import React, { useEffect } from 'react';
import { useColorPalette } from '../../hooks/useColorPalette';

/**
 * Modal che mostra la conferma prima dell'eliminazione di un contatto
 * @param {boolean} isOpen - Stato di apertura della modale
 * @param {function} toggle - Funzione per chiudere/aprire la modale
 * @param {object} contactToDelete - Dati del contatto da eliminare
 * @param {function} onConfirm - Funzione da eseguire alla conferma dell'eliminazione
 */
const ContactDeleteModal = ({ isOpen, toggle, contactToDelete = {}, onConfirm }) => {
    // Utilizzo dell'hook per accedere alla palette di colori
    const palette = useColorPalette();
    
    // Log per tracciare le aperture e chiusure della modale
    useEffect(() => {
        console.log('[DEBUG] ContactDeleteModal - Stato modale cambiato:', { 
            isOpen, 
            contactToDelete 
        });
        
        if (isOpen && !contactToDelete) {
            console.warn('[DEBUG] ContactDeleteModal - Modale aperta ma senza dati di contatto!');
        }
    }, [isOpen, contactToDelete]);

    console.log('[DEBUG] ContactDeleteModal - Rendering modale con:', { isOpen });
    
    // Se la modale non è aperta, non renderizziamo nulla
    if (!isOpen) return null;
    
    // Utilizziamo un approccio diretto con CSS in-line per garantire la visibilità
    const modalStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
    };
    
    const modalContentStyles = {
        backgroundColor: 'white',
        borderRadius: '5px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    };

    return (
        <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={modalContentStyles}>
                {/* Header */}
                <div style={{ 
                    backgroundColor: palette.secondary, 
                    color: 'white', 
                    padding: '1rem', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTopLeftRadius: '5px',
                    borderTopRightRadius: '5px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="fa-solid fa-exclamation-triangle me-2 text-warning"></i>
                        <span className="fw-bold">
                            Conferma eliminazione contatto
                        </span>
                    </div>
                    <button 
                        onClick={toggle} 
                        style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            color: 'white',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        &times;
                    </button>
                </div>
                
                {/* Body */}
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.5rem'
                    }}>
                        <i className="fa-solid fa-trash" style={{ 
                            fontSize: '2rem', 
                            color: palette.danger || '#dc3545',
                            marginRight: '1rem' 
                        }}></i>
                        <h5 style={{ margin: 0 }}>Stai per eliminare questo contatto</h5>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '5px',
                        padding: '1rem',
                        marginBottom: '1.5rem',
                        border: '1px solid #dee2e6'
                    }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            Dettagli contatto:
                        </p>
                        <ul style={{ 
                            listStyleType: 'none',
                            padding: '0',
                            margin: '0'
                        }}>
                            <li><strong>Nome:</strong> {contactToDelete?.name || 'N/D'} {contactToDelete?.surname || ''}</li>
                            {contactToDelete?.role && <li><strong>Ruolo:</strong> {contactToDelete.role}</li>}
                            {contactToDelete?.contactTypeLabel && <li><strong>Tipo:</strong> {contactToDelete.contactTypeLabel}</li>}
                            {contactToDelete?.email && <li><strong>Email:</strong> {contactToDelete.email}</li>}
                            {contactToDelete?.phone && <li><strong>Telefono:</strong> {contactToDelete.phone}</li>}
                            {contactToDelete?.mobile && <li><strong>Cellulare:</strong> {contactToDelete.mobile}</li>}
                            <li>
                                <strong>Area riservata:</strong> 
                                {contactToDelete?.enabledToReservedArea ? ' Abilitato' : ' Non abilitato'}
                            </li>
                        </ul>
                    </div>
                    
                    {contactToDelete?.enabledToReservedArea && (
                        <div style={{ 
                            backgroundColor: '#fff3cd', 
                            color: '#856404', 
                            padding: '1rem', 
                            borderRadius: '5px',
                            marginBottom: '1.5rem',
                            border: '1px solid #ffeeba'
                        }}>
                            <i className="fa-solid fa-exclamation-circle me-2"></i>
                            <strong>Attenzione:</strong> Questo contatto ha accesso all'area riservata. 
                            Eliminandolo, rimuoverai anche il suo accesso alla piattaforma.
                        </div>
                    )}
                    
                    {/* Messaggio comune per tutti i casi */}
                    <div style={{ 
                        backgroundColor: '#e2f0fd', 
                        color: '#0c5460', 
                        padding: '1rem', 
                        borderRadius: '5px',
                        border: '1px solid #bee5eb'
                    }}>
                        <i className="fa-solid fa-info-circle me-2"></i>
                        <div style={{ fontWeight: 'bold' }}>
                            Per completare effettivamente l'eliminazione del contatto devi cliccare sul pulsante "Modifica cliente" in fondo alla pagina.
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                <div style={{ 
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                    borderTop: '1px solid #dee2e6'
                }}>
                    <button 
                        onClick={() => {
                            console.log('[DEBUG] ContactDeleteModal - Annulla cliccato');
                            toggle();
                        }}
                        style={{ 
                            backgroundColor: '#6c757d', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fa-solid fa-times me-2"></i>
                        Annulla
                    </button>
                    <button 
                        onClick={() => {
                            console.log('[DEBUG] ContactDeleteModal - Elimina cliccato');
                            onConfirm();
                            toggle();
                        }}
                        style={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fa-solid fa-trash me-2"></i>
                        Elimina comunque
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactDeleteModal;
